import { ContactEndPoint } from '@/modules/common/enums/end-point.ts';
import Api from '@/modules/core/api/api.ts';
import { TableId } from '@/modules/common/enums/table-id.enum.ts';
import { unref } from 'vue';
import type { ItemsDto } from '@/modules/common/dto/items.dto.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import TypeOption from '@/modules/common/models/option/type-option.ts';
import type { ContactMaskDto } from '@/modules/counterparty/dto/contact-mask.dto.ts';
import { CLIENTS_CONTACTS_GET } from '@/modules/core/configs/end-points.ts';
import GatewayApi from '@/modules/core/api/gateway.api.ts';
import { GatewayFilter } from '@/modules/core/utils/gateway-filter.ts';
import { Direction } from '@/modules/common/enums/direction.enum.ts';
import type { CounterpartyContact } from '@/modules/counterparty/types/counterparty-contact.type.ts';

export interface ContactFormData {
  type: Option;
  contact: string;
  comment: string;
  fio?: string;
  position?: string;
  birthDay?: string | null;
  mask?: string | null;
}

export type ContactRepresentativeOption = Option<string> & {
  position: string;
  birthDay: string | null;
};

type RawContactsResponse = { data: ItemsDto<CounterpartyContact> };

const REPRESENTATIVE_SEARCH_LIMIT = 50;

function normalizeLabel(value: string) {
  return value.replace(/\s+/gu, ' ').trim().toLowerCase();
}

interface AddRequest {
  parentId: number;
  formData: ContactFormData;
}

interface EditRequest {
  id: number;
  formData: ContactFormData;
}

function buildExtPayload(data: ContactFormData) {
  return {
    contact_type: data.type.label,
    contact: data.contact,
    comment: data.comment || undefined,
    fio: data.fio || undefined,
    position: data.position || undefined,
    birth_day: data.birthDay || undefined,
    mask: data.mask ?? '',
  };
}

export default class ContactApi {
  static async getTypes(signal: AbortSignal): Promise<TypeOption[]> {
    const response = await Api.post(ContactEndPoint.GetTypes, {}, signal);
    return response.items.map((item: unknown) => new TypeOption(item));
  }

  static async getMasks(signal: AbortSignal): Promise<Map<string, string>> {
    const response: ItemsDto<ContactMaskDto> = await Api.post(ContactEndPoint.GetMasks, {}, signal);
    const masks = new Map<string, string>();

    for (const item of response.items ?? []) {
      const type = item.Тип?.trim();
      const mask = item.Маска?.trim();

      if (!type || !mask) {
        continue;
      }

      masks.set(type, mask);
    }

    return masks;
  }

  static async searchRepresentatives(query: string, signal: AbortSignal): Promise<ContactRepresentativeOption[]> {
    const trimmed = query.trim();
    const response = await GatewayApi.execute<RawContactsResponse>(CLIENTS_CONTACTS_GET, {
      filter: GatewayFilter.and(GatewayFilter.notEmpty('ФИО'), trimmed ? GatewayFilter.contains('ФИО', trimmed) : null),
      limit: REPRESENTATIVE_SEARCH_LIMIT,
      orderBy: [{ field: 'ФИО', direction: Direction.Asc }],
      signal,
      mapData: false,
    });

    const seen = new Set<string>();
    const options: ContactRepresentativeOption[] = [];

    for (const item of response.data?.items ?? []) {
      const fio = item.ФИО?.trim() ?? '';

      if (!fio) {
        continue;
      }

      const key = normalizeLabel(fio);

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      const position = item.Должность?.trim() ?? '';
      options.push({
        code: String(item.CONTACT_ID),
        label: fio,
        position,
        birthDay: item.ДР || null,
      });
    }

    return options;
  }

  static async searchPositions(query: string, signal: AbortSignal): Promise<Option[]> {
    const trimmed = query.trim();
    const response = await GatewayApi.execute<RawContactsResponse>(CLIENTS_CONTACTS_GET, {
      filter: GatewayFilter.and(
        GatewayFilter.notEmpty('Должность'),
        trimmed ? GatewayFilter.contains('Должность', trimmed) : null
      ),
      limit: REPRESENTATIVE_SEARCH_LIMIT,
      orderBy: [{ field: 'Должность', direction: Direction.Asc }],
      signal,
      mapData: false,
    });

    const seen = new Set<string>();
    const options: Option[] = [];

    for (const item of response.data?.items ?? []) {
      const label = item.Должность?.trim() ?? '';

      if (!label) {
        continue;
      }

      const key = normalizeLabel(label);

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      options.push({ code: label, label });
    }

    return options;
  }

  static async add({ parentId, formData }: AddRequest) {
    const data = unref(formData);

    await Api.post(ContactEndPoint.Add, {
      parent_table_id: TableId.Counterparties,
      parent_id: parentId,
      contact_type: data.type.label,
      contact: data.contact,
      comment: data.comment || undefined,
      mask: data.mask ?? '',
    });
  }

  static async addExt({ parentId, formData }: AddRequest) {
    const data = unref(formData);

    await Api.post(ContactEndPoint.ExtAdd, {
      parent_table_id: TableId.Counterparties,
      parent_id: parentId,
      ...buildExtPayload(data),
    });
  }

  static async edit({ id, formData }: EditRequest) {
    const data = unref(formData);

    await Api.post(ContactEndPoint.Edit, {
      contact_id: id,
      contact_type: data.type.label,
      contact: data.contact,
      comment: data.comment || undefined,
      mask: data.mask ?? '',
    });
  }

  static async editExt({ id, formData }: EditRequest) {
    const data = unref(formData);

    await Api.post(ContactEndPoint.ExtEdit, {
      contact_id: id,
      ...buildExtPayload(data),
    });
  }

  static async delete(id: number) {
    await Api.post(ContactEndPoint.Delete, { contact_id: id });
  }
}
